export const POPULAR_MAKES = [
  'Toyota', 'BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen',
  'Ford', 'Honda', 'Hyundai', 'Kia', 'Nissan', 'Opel', 'Skoda',
  'Peugeot', 'Renault', 'Fiat', 'Mazda',
];

// Filter State Interface
export interface FilterState {
  // Body Type
  bodyType: string;

  // Condition
  condition: string;

  // Make, Model, Version
  make: string;
  model: string;
  version: string;

  // Price & mileage filters
  priceFrom: string;
  priceTo: string;
  yearFrom: string;
  yearTo: string;
  mileageFrom: string;
  mileageTo: string;

  // Location
  location: string;
  city: string;
  country: string;

  // Color
  color: string;
  isMetallic: boolean;

  // Availability
  availability: string;

  // Seller Type
  sellerType: string;

  // Dealer Rating
  dealerRating: string;

  // Fuel Type
  fuelType: string;

  // Price Tag
  priceTag: string;

  // Sorting
  sortBy: string;

  // Search Query
  searchQuery: string;

  // Imported Vehicles
  isImported: boolean;
  importCountry: string;
  importAvailability: '' | 'AVAILABLE_NOW' | 'READY_TO_IMPORT';
}

// Default Filter State
export const defaultFilterState: FilterState = {
  bodyType: '',
  condition: '',
  make: '',
  model: '',
  version: '',
  priceFrom: '',
  priceTo: '',
  yearFrom: '',
  yearTo: '',
  mileageFrom: '',
  mileageTo: '',
  location: '',
  city: '',
  country: '',
  color: '',
  isMetallic: false,
  availability: '',
  sellerType: '',
  dealerRating: '',
  fuelType: '',
  priceTag: '',
  sortBy: 'standard',
  searchQuery: '',
  isImported: false,
  importCountry: '',
  importAvailability: '',
};

// Color Options
export const colors = [
  '#1d1d1d',
  '#ffffff',
  '#b4b4b4',
  '#808080',
  '#d7b48c',
  '#8a5b24',
  '#ff3b30',
  '#ff7b00',
  '#ffcc00',
  '#3366ff',
  '#6cb33f',
  '#d4c542',
  '#9f77ff',
  'linear-gradient(135deg,#eee,#bbb,#eee)',
];

// Condition Options
export const conditionOptions = [
  { label: 'Any', value: '' },
  { label: 'New', value: 'NEW' },
  { label: 'Used', value: 'USED' },
];

// Availability Options
export const availabilityOptions = [
  { label: 'Any', value: '' },
  { label: 'Available Now', value: 'available_now' },
  { label: 'With delivery time', value: 'with_delivery_time' },
];

// Seller Type Options
export const sellerTypeOptions = [
  { label: 'Any', value: '' },
  { label: 'Dealer', value: 'DEALER' },
  { label: 'Private Seller', value: 'PRIVATE' },
];

// Dealer Rating Options
export const dealerRatingOptions = [
  { label: 'Any', value: '' },
  { label: 'from 3 stars (2,345)', value: '3' },
  { label: '4 stars and up (1,876)', value: '4' },
  { label: '5 stars (1,234)', value: '5' },
];

// Sort Options
export const sortOptions = [
  { label: '✦ Smart Ranking', value: 'standard' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: 'Newest First', value: 'year_desc' },
  { label: 'Lowest Mileage', value: 'mileage_asc' },
  { label: 'Highest Mileage', value: 'mileage_desc' },
];

// Fuel Type Display Map
export const fuelTypeMap: { [key: string]: string } = {
  PETROL: 'Petrol',
  DIESEL: 'Diesel',
  ELECTRIC: 'Electric',
  HYBRID: 'Hybrid',
  PLUGIN_HYBRID: 'Plug-in Hybrid',
};

// Fuel Type Options
export const fuelTypeOptions = [
  { label: 'Any', value: '' },
  { label: 'Petrol', value: 'PETROL' },
  { label: 'Diesel', value: 'DIESEL' },
  { label: 'Electric', value: 'ELECTRIC' },
  { label: 'Hybrid', value: 'HYBRID' },
  { label: 'Plug-in Hybrid', value: 'PLUGIN_HYBRID' },
];

// Pagination State Type
export interface PaginationState {
  page: number;
  limit: number;
  totalPages?: number;
}

// Pagination Defaults (only page and limit - totalPages comes from backend)
export const defaultPagination: PaginationState = {
  page: 1,
  limit: 15,
};

// Body Type Options (matches backend Listing.bodyType field)
export const bodyTypeOptions = [
  { label: 'Any', value: '' },
  { label: 'Sedan', value: 'SEDAN' },
  { label: 'SUV', value: 'SUV' },
  { label: 'Hatchback', value: 'HATCHBACK' },
  { label: 'Coupe', value: 'COUPE' },
  { label: 'Convertible', value: 'CONVERTIBLE' },
  { label: 'Wagon', value: 'WAGON' },
  { label: 'Pickup', value: 'PICKUP' },
  { label: 'Van', value: 'VAN' },
  { label: 'Minivan', value: 'MINIVAN' },
  { label: 'Truck', value: 'TRUCK' },
];

// API Query Parameters Type
export interface QueryParams {
  page: number;
  limit: number;
  bodyType?: string;
  condition?: string;
  make?: string;
  model?: string;
  version?: string;
  priceFrom?: number;
  priceTo?: number;
  yearFrom?: number;
  yearTo?: number;
  mileageFrom?: number;
  mileageTo?: number;
  location?: string;
  city?: string;
  country?: string;
  color?: string;
  isMetallic?: boolean;
  availability?: string;
  sellerType?: string;
  dealerRating?: string;
  fuelType?: string;
  sortBy?: string;
  searchQuery?: string;
  isImported?: boolean;
  importCountry?: string;
  importAvailability?: 'AVAILABLE_NOW' | 'READY_TO_IMPORT';
}

// Build query parameters from filters and pagination
export const buildQueryParams = (
  filterState: FilterState,
  paginationState: PaginationState
): QueryParams => {
  const params: any = {
    page: paginationState.page,
    limit: paginationState.limit,
  };

  // Body Type
  if (filterState.bodyType) params.bodyType = filterState.bodyType;

  // Condition
  if (filterState.condition) params.condition = filterState.condition;

  // Make, Model, Version
  if (filterState.make) params.make = filterState.make;
  if (filterState.model) params.model = filterState.model;
  if (filterState.version) params.version = filterState.version;

  // Price & mileage filters
  if (filterState.priceFrom)
    params.priceFrom = parseFloat(filterState.priceFrom);
  if (filterState.priceTo) params.priceTo = parseFloat(filterState.priceTo);
  if (filterState.yearFrom) params.yearFrom = parseInt(filterState.yearFrom);
  if (filterState.yearTo) params.yearTo = parseInt(filterState.yearTo);
  if (filterState.mileageFrom)
    params.mileageFrom = parseFloat(filterState.mileageFrom);
  if (filterState.mileageTo)
    params.mileageTo = parseFloat(filterState.mileageTo);

  // Location
  if (filterState.location) params.location = filterState.location;
  if (filterState.city) params.city = filterState.city;
  if (filterState.country) params.country = filterState.country;

  // Color
  if (filterState.color) params.color = filterState.color;
  if (filterState.isMetallic) params.isMetallic = filterState.isMetallic;

  // Availability
  if (filterState.availability) params.availability = filterState.availability;

  // Seller Type
  if (filterState.sellerType) params.sellerType = filterState.sellerType;

  // Dealer Rating
  if (filterState.dealerRating) params.dealerRating = filterState.dealerRating;

  // Fuel Type
  if (filterState.fuelType) params.fuelType = filterState.fuelType;

  // Price Tag
  if (filterState.priceTag) params.priceTag = filterState.priceTag;

  // Sorting
  if (filterState.sortBy && filterState.sortBy !== 'standard')
    params.sortBy = filterState.sortBy;

  // Search Query
  if (filterState.searchQuery && filterState.searchQuery.trim())
    params.searchQuery = filterState.searchQuery.trim();

  // Imported
  if (filterState.isImported) params.isImported = true;
  if (filterState.isImported && filterState.importCountry)
    params.importCountry = filterState.importCountry;
  if (filterState.isImported && filterState.importAvailability)
    params.importAvailability = filterState.importAvailability as
      | 'AVAILABLE_NOW'
      | 'READY_TO_IMPORT';

  return params;
};

// Re-export formatting utilities from utils
export { formatPrice, formatMileage } from '../utils/formatters';

export const getFuelTypeDisplay = (fuelType: string): string => {
  return fuelTypeMap[fuelType] || fuelType;
};
